import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "../../store/authStore";
import { Pencil, MessageCircle, Boxes } from "lucide-react";
import TeamManageDialog from "../teams/TeamManageDialog";
import { useViewScopeStore } from "../../store/viewScopeStore";
import { api } from "../../lib/api";
import { TEST_IDS } from "../../constants/testIds";
import { getListPalette } from "../../constants/listPalette";
import {
  LayoutDashboard,
  FolderKanban,
  Users,
  Settings as SettingsIcon,
  CreditCard,
  ShieldCheck,
  LogOut,
  Bell,
  UserPlus2,
  Plug,
  Hash,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Zap,
  StickyNote,
  Trophy,
  CalendarDays,
  CalendarClock,
  Contact2,
  Building2,
  Briefcase,
  Tent,
  LayoutTemplate,
  Users2,
  TrendingUp,
  ClipboardList,
  HardHat,
  FileText,
  FolderOpen,
  Flag,
  NotebookPen,
  Network,
  ListChecks,
  X,
} from "lucide-react";
import { SerpixWordmark } from "../brand/SerpixWordmark";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const NavItem = ({ to, icon: Icon, label, testId }) => (
  <NavLink
    to={to}
    data-testid={testId}
    className={({ isActive }) =>
      [
        "flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
        isActive
          ? "bg-zinc-900 text-white"
          : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
      ].join(" ")
    }
  >
    <Icon className="h-4 w-4" strokeWidth={1.75} />
    <span>{label}</span>
  </NavLink>
);

function ExpoSection() {
  // "EXPO" feature-flagged workspace grouping (fuar/expo operations).
  const { user } = useAuthStore();
  const isAdmin = ["super_admin", "ultra_admin"].includes(user?.role);
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("sidebar.expo.open") !== "0";
  });
  const toggle = () =>
    setOpen((v) => {
      try {
        window.localStorage.setItem("sidebar.expo.open", v ? "0" : "1");
      } catch {
        /* ignore */
      }
      return !v;
    });
  const subLinkCls = ({ isActive }) =>
    [
      "flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors",
      isActive
        ? "bg-zinc-900 text-white"
        : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
    ].join(" ");
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={toggle}
        data-testid="sidebar-expo-header"
        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
      >
        <Tent className="h-4 w-4" strokeWidth={1.75} />
        <span className="flex-1 text-left font-semibold uppercase tracking-wider text-[11px]">
          EXPO
        </span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
        )}
      </button>
      {open && (
        <div className="ml-2 mt-0.5 flex flex-col gap-0.5 border-l border-zinc-200 pl-2">
          <NavLink
            to="/app/expo/fair-projects"
            data-testid="sidebar-expo-fair-projects"
            className={subLinkCls}
          >
            <Flag className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>Fuarlar To Do List</span>
          </NavLink>
          <NavLink
            to="/app/expo/fairs"
            data-testid="sidebar-expo-fairs"
            className={subLinkCls}
          >
            <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>Fuar Takvimi</span>
          </NavLink>
          <NavLink
            to="/app/expo/files"
            data-testid="sidebar-expo-files"
            className={subLinkCls}
          >
            <FolderOpen className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>Dosyalar</span>
          </NavLink>
          {isAdmin && (
            <NavLink
              to="/app/expo/questions"
              data-testid="sidebar-expo-questions"
              className={subLinkCls}
            >
              <ListChecks className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span>Soru Yönetimi</span>
            </NavLink>
          )}
          {isAdmin && (
            <NavLink
              to="/app/expo/templates"
              data-testid="sidebar-expo-templates"
              className={subLinkCls}
            >
              <LayoutTemplate className="h-3.5 w-3.5" strokeWidth={1.75} />
              <span>Şablon Yönetimi</span>
            </NavLink>
          )}
        </div>
      )}
    </div>
  );
}

function CopSection({ t }) {
  // "COP" is a feature-flagged workspace grouping. Hosts 4 first-class
  // modules — Takımlar, Satış Pipeline, Faturalar, Dosyalar — plus a
  // nested Kontaklar sub-tree. Visible only when the tenant has the
  // "cop" flag on. Collapse states persist to localStorage.
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("sidebar.cop.open") !== "0";
  });
  const [contactsOpen, setContactsOpen] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("sidebar.contacts.open") === "1";
  });
  const persist = (key, val) => {
    try {
      window.localStorage.setItem(key, val ? "1" : "0");
    } catch {
      /* ignore */
    }
  };
  const toggle = () => setOpen((v) => (persist("sidebar.cop.open", !v), !v));
  const toggleContacts = () =>
    setContactsOpen((v) => (persist("sidebar.contacts.open", !v), !v));

  const subLinkCls = ({ isActive }) =>
    [
      "flex items-center gap-2 rounded-md px-2 py-1.5 text-[13px] transition-colors",
      isActive
        ? "bg-zinc-900 text-white"
        : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
    ].join(" ");

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={toggle}
        data-testid="sidebar-cop-header"
        className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
      >
        <Briefcase className="h-4 w-4" strokeWidth={1.75} />
        <span className="flex-1 text-left font-semibold uppercase tracking-wider text-[11px]">
          COP 31
        </span>
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-zinc-400" />
        )}
      </button>
      {open && (
        <div className="ml-2 mt-0.5 border-l border-zinc-200 pl-2">
          <NavLink
            to="/app/cop/dashboard"
            data-testid="sidebar-cop-dashboard"
            className={subLinkCls}
          >
            <LayoutDashboard className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>Yönetim Panosu</span>
          </NavLink>
          <NavLink
            to="/app/cop/teams"
            data-testid="sidebar-cop-teams"
            className={subLinkCls}
          >
            <Users2 className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>{t("nav.cop_teams")}</span>
          </NavLink>
          <NavLink
            to="/app/cop/deals"
            data-testid="sidebar-cop-deals"
            className={subLinkCls}
          >
            <TrendingUp className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>{t("nav.cop_deals")}</span>
          </NavLink>
          <NavLink
            to="/app/cop/construction"
            data-testid="sidebar-cop-construction"
            className={subLinkCls}
          >
            <HardHat className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>{t("nav.cop_construction")}</span>
          </NavLink>
          <NavLink
            to="/app/cop/construction-tracking"
            data-testid="sidebar-cop-construction-tracking"
            className={subLinkCls}
          >
            <ClipboardList className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>{t("nav.cop_construction_tracking")}</span>
          </NavLink>
          <NavLink
            to="/app/cop/hubs"
            data-testid="sidebar-cop-hubs"
            className={subLinkCls}
          >
            <Boxes className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>Hub Yönetimi</span>
          </NavLink>
          <NavLink
            to="/app/cop/invoices"
            data-testid="sidebar-cop-invoices"
            className={subLinkCls}
          >
            <FileText className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>{t("nav.cop_invoices")}</span>
          </NavLink>
          <NavLink
            to="/app/cop/files"
            data-testid="sidebar-cop-files"
            className={subLinkCls}
          >
            <FolderOpen className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span>{t("nav.cop_files")}</span>
          </NavLink>
          {/* Kontaklar sub-group */}
          <button
            type="button"
            onClick={toggleContacts}
            data-testid="sidebar-contacts-header"
            className="mt-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-[13px] text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          >
            <Contact2 className="h-3.5 w-3.5" strokeWidth={1.75} />
            <span className="flex-1 text-left">{t("nav.contacts")}</span>
            {contactsOpen ? (
              <ChevronDown className="h-3 w-3 text-zinc-400" />
            ) : (
              <ChevronRight className="h-3 w-3 text-zinc-400" />
            )}
          </button>
          {contactsOpen && (
            <div className="ml-2 mt-0.5 flex flex-col gap-0.5 border-l border-zinc-100 pl-2">
              <NavLink
                to="/app/contacts/people"
                data-testid="sidebar-contacts-people"
                className={subLinkCls}
              >
                <UserPlus2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                <span>{t("nav.contacts_people")}</span>
              </NavLink>
              <NavLink
                to="/app/contacts/companies"
                data-testid="sidebar-contacts-companies"
                className={subLinkCls}
              >
                <Building2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                <span>{t("nav.contacts_companies")}</span>
              </NavLink>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TeamsSection({ projects }) {
  // "Takımlar" — bir To Do List'e (projeye) bağlı, 2+ üyeli takımlar.
  // Her satır takım adı + bağlı liste adı gösterir, listeye tıklanarak gidilir.
  const { scope } = useViewScopeStore();
  const isMe = scope === "me";
  const { user } = useAuthStore();
  const canManage =
    ["super_admin", "ultra_admin"].includes(user?.role) ||
    user?.can_manage_contacts !== false;
  const [teams, setTeams] = useState([]);
  const [manageTeam, setManageTeam] = useState(null);
  const [open, setOpen] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.localStorage.getItem("sidebar.teams.open") !== "0";
  });
  const toggle = () =>
    setOpen((v) => {
      try {
        window.localStorage.setItem("sidebar.teams.open", v ? "0" : "1");
      } catch {
        /* ignore */
      }
      return !v;
    });
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/teams");
        if (!cancelled)
          setTeams(
            (data || []).filter(
              (tm) => tm.project_id && (tm.member_ids || []).length > 1,
            ),
          );
      } catch {
        if (!cancelled) setTeams([]);
      }
    };
    load();
    const onChanged = () => load();
    window.addEventListener("serpix:projects-changed", onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("serpix:projects-changed", onChanged);
    };
  }, []);

  const projById = Object.fromEntries((projects || []).map((p) => [p.id, p]));
  const rows = teams.filter((tm) => projById[tm.project_id]);
  if (rows.length === 0) return null;
  return (
    <div className="mt-4 border-t border-zinc-100 pt-3">
      <button
        type="button"
        onClick={toggle}
        data-testid="sidebar-teams-header"
        className="flex w-full items-center gap-1 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-700"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        <span>TAKIMLAR</span>
        <span className="ml-1 text-zinc-400">{rows.length}</span>
      </button>
      {open && (
        <ul className="mt-1 flex flex-col gap-0.5" data-testid="sidebar-teams-list">
          {rows.map((tm) => {
            const p = projById[tm.project_id];
            const cnt = isMe ? p.open_for_me_count || 0 : p.open_count || 0;
            const overdue = isMe ? !!p.has_overdue_for_me : !!p.has_overdue;
            return (
              <li key={tm.id} className="group/team flex items-center gap-0.5">
                <NavLink
                  to={`/app/projects/${tm.project_id}`}
                  data-testid={`sidebar-team-${tm.id}`}
                  className={({ isActive }) =>
                    [
                      "flex min-w-0 flex-1 items-center gap-2 rounded-md px-3 py-1.5 transition-colors",
                      isActive
                        ? "bg-zinc-900 text-white"
                        : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
                    ].join(" ")
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Users2 className="h-3.5 w-3.5 shrink-0" strokeWidth={1.75} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13px] font-medium">
                          {tm.name}
                        </span>
                        <span
                          className={[
                            "block truncate text-[10px]",
                            isActive ? "text-zinc-300" : "text-zinc-400",
                          ].join(" ")}
                        >
                          # {p.name}
                        </span>
                      </span>
                      <span className="ml-auto flex shrink-0 items-center gap-1">
                        {cnt > 0 && (
                          <span
                            data-testid={`sidebar-team-badge-${tm.id}`}
                            className={[
                              "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold",
                              isActive
                                ? "bg-white text-zinc-900"
                                : "bg-amber-500 text-white",
                            ].join(" ")}
                            title={`${cnt} tamamlanmamış görev`}
                          >
                            {cnt}
                          </span>
                        )}
                        {overdue && (
                          <Zap
                            className="h-3.5 w-3.5 fill-red-500 text-red-600"
                            strokeWidth={2}
                          />
                        )}
                      </span>
                    </>
                  )}
                </NavLink>
                <button
                  type="button"
                  onClick={() =>
                    window.dispatchEvent(
                      new CustomEvent("dm-open", {
                        detail: {
                          kind: "channel",
                          id: tm.project_id,
                          name: `${tm.name} · ${p.name}`,
                        },
                      }),
                    )
                  }
                  data-testid={`sidebar-team-chat-${tm.id}`}
                  title="Takım sohbeti"
                  className="flex h-7 w-6 shrink-0 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-violet-50 hover:text-violet-600"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </button>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => setManageTeam(tm)}
                    data-testid={`sidebar-team-manage-${tm.id}`}
                    title="Takımı yönet"
                    className="flex h-7 w-6 shrink-0 items-center justify-center rounded text-zinc-300 opacity-0 transition-opacity hover:bg-zinc-100 hover:text-zinc-700 group-hover/team:opacity-100"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {manageTeam && (
        <TeamManageDialog
          team={manageTeam}
          projectName={projById[manageTeam.project_id]?.name}
          onClose={(changed) => {
            setManageTeam(null);
            if (changed)
              window.dispatchEvent(new Event("serpix:projects-changed"));
          }}
        />
      )}
    </div>
  );
}

export default function Sidebar({ open = false, onClose }) {
  const { t } = useTranslation();
  const { user, tenant, logout } = useAuthStore();
  const { scope } = useViewScopeStore();
  const isMe = scope === "me";
  const navigate = useNavigate();
  const location = useLocation();

  const isPlatformAdmin = user?.role === "platform_admin";
  const isSuperAdmin = user?.role === "super_admin" || user?.role === "ultra_admin";
  // Show tenant project list to everyone except platform admins (who operate
  // at platform level, not inside a tenant).
  const showProjectList = !isPlatformAdmin;
  // Feature flags come from the tenant record via /auth/me. Default to []
  // so legacy tenants never trigger the module.
  const hasCop = Array.isArray(tenant?.enabled_features)
    ? tenant.enabled_features.includes("cop")
    : false;
  const hasExpo = Array.isArray(tenant?.enabled_features)
    ? tenant.enabled_features.includes("expo")
    : false;

  // Lazy-loaded sidebar project list for non-admin users (Slack-style
  // channel list). Super admins already have a dedicated Projects page
  // and can create/delete projects, so we don't clutter their nav.
  const [projects, setProjects] = useState([]);
  const [projectsOpen, setProjectsOpen] = useState(true);
  // Which projects are expanded to show their project-groups (lists).
  const [expandedProjects, setExpandedProjects] = useState(() => new Set());
  // Cache: {projectId: [lists]}
  const [projectLists, setProjectLists] = useState({});
  const [listsLoading, setListsLoading] = useState({});
  useEffect(() => {
    if (!showProjectList) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/projects");
        if (!cancelled) setProjects(data || []);
      } catch {
        if (!cancelled) setProjects([]);
      }
    };
    load();
    // Re-fetch whenever a project is created/deleted/restored anywhere in
    // the app so the sidebar stays in sync without a full page reload.
    const onChanged = () => load();
    window.addEventListener("serpix:projects-changed", onChanged);
    return () => {
      cancelled = true;
      window.removeEventListener("serpix:projects-changed", onChanged);
    };
  }, [showProjectList]);

  const loadListsFor = useCallback(
    async (projectId, { force = false } = {}) => {
      if (!force && (projectLists[projectId] || listsLoading[projectId])) return;
      setListsLoading((m) => ({ ...m, [projectId]: true }));
      try {
        const { data } = await api.get(`/lists?project_id=${projectId}`);
        setProjectLists((m) => ({ ...m, [projectId]: data || [] }));
      } catch {
        setProjectLists((m) => ({ ...m, [projectId]: [] }));
      } finally {
        setListsLoading((m) => ({ ...m, [projectId]: false }));
      }
    },
    [projectLists, listsLoading],
  );

  // React to project-group (list) mutations happening elsewhere in the app
  // — e.g. ProjectDetail creates/deletes a group. We refetch lists for
  // every project we've already loaded so the sidebar tree stays in sync
  // without a full page reload.
  useEffect(() => {
    const onListsChanged = () => {
      Object.keys(projectLists).forEach((pid) => loadListsFor(pid, { force: true }));
    };
    window.addEventListener("serpix:lists-changed", onListsChanged);
    return () =>
      window.removeEventListener("serpix:lists-changed", onListsChanged);
  }, [projectLists, loadListsFor]);

  const toggleProjectExpansion = (projectId) => {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
        loadListsFor(projectId);
      }
      return next;
    });
  };

  // Parse ?list=xxx from the current URL so the active chip highlights.
  const activeListId = (() => {
    const params = new URLSearchParams(location.search);
    return params.get("list") || null;
  })();

  // Refresh lists cache for the project the user is currently viewing —
  // ensures newly created groups appear immediately in the sidebar.
  useEffect(() => {
    const m = location.pathname.match(/^\/app\/projects\/([^/]+)/);
    const pid = m?.[1];
    if (!pid) return;
    (async () => {
      try {
        const { data } = await api.get(
          `/lists?project_id=${pid}`,
        );
        setProjectLists((mp) => ({ ...mp, [pid]: data || [] }));
        setExpandedProjects((prev) => {
          if (prev.has(pid)) return prev;
          const next = new Set(prev);
          next.add(pid);
          return next;
        });
      } catch {
        /* ignore */
      }
    })();
  }, [location.pathname]);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <aside
      className={[
        "fixed left-0 top-0 z-40 flex h-[100dvh] w-72 max-w-[85vw] flex-col border-r border-zinc-200 bg-white p-5 pb-[max(1rem,env(safe-area-inset-bottom))] transition-transform duration-300 md:h-screen md:w-64 md:max-w-none md:translate-x-0 md:pb-5",
        open ? "translate-x-0 shadow-2xl" : "-translate-x-full",
      ].join(" ")}
      style={{ paddingTop: "max(1.25rem, env(safe-area-inset-top))" }}
      data-testid="app-sidebar"
    >
      <div className="mb-6 flex items-center justify-between md:mb-8">
        <SerpixWordmark size="sm" />
        {/* Mobile close button */}
        <button
          type="button"
          onClick={onClose}
          data-testid="sidebar-close-btn"
          aria-label="Close menu"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-zinc-200 text-zinc-500 transition-colors hover:border-zinc-300 hover:text-zinc-900 md:hidden"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
        {!isPlatformAdmin && (
          <>
            <NavItem
              to="/app/dashboard"
              icon={LayoutDashboard}
              label={t("nav.dashboard")}
              testId={TEST_IDS.sidebarDashboard}
            />
            <NavItem
              to="/app/projects"
              icon={FolderKanban}
              label={t("nav.projects")}
              testId={TEST_IDS.sidebarProjects}
            />
            <NavItem
              to="/app/tasks"
              icon={ClipboardList}
              label="Görevlerim"
              testId="sidebar-tasks-hub"
            />
            <NavItem
              to="/app/quick-notes"
              icon={StickyNote}
              label={t("nav.quick_note")}
              testId="sidebar-quick-notes"
            />
            <NavItem
              to="/app/notes"
              icon={NotebookPen}
              label="Notlar"
              testId="sidebar-notes"
            />
            <NavItem
              to="/app/takvim"
              icon={CalendarDays}
              label={t("nav.calendar")}
              testId="sidebar-calendar"
            />
            <NavItem
              to="/app/my-activities"
              icon={CalendarClock}
              label={t("nav.my_activities")}
              testId="sidebar-my-activities"
            />
            <NavItem
              to="/app/motivasyon"
              icon={Trophy}
              label={t("nav.motivation")}
              testId="sidebar-motivation"
            />
            <NavItem
              to="/app/org-chart"
              icon={Network}
              label="Organizasyon Şeması"
              testId="sidebar-org-chart"
            />
            {hasCop && <CopSection t={t} />}
            {hasExpo && <ExpoSection />}
          </>
        )}
        {isPlatformAdmin && (
          <NavItem
            to="/app/platform"
            icon={ShieldCheck}
            label={t("nav.platform")}
            testId={TEST_IDS.sidebarPlatform}
          />
        )}

        {showProjectList && (
          <div className="mt-4 border-t border-zinc-100 pt-3">
            <div className="flex items-center gap-1 px-3 py-1">
              <button
                type="button"
                onClick={() => setProjectsOpen((v) => !v)}
                data-testid="sidebar-projects-header"
                className="flex flex-1 items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-700"
              >
                {projectsOpen ? (
                  <ChevronDown className="h-3 w-3" />
                ) : (
                  <ChevronRight className="h-3 w-3" />
                )}
                <span>TO DO LISTS</span>
                <span className="ml-1 text-zinc-400">{projects.length}</span>
              </button>
              <NavLink
                to="/app/takip"
                data-testid="sidebar-takip-dashboard"
                title="Takip Dashboard"
                className={({ isActive }) =>
                  [
                    "flex h-6 w-6 shrink-0 items-center justify-center rounded transition-colors",
                    isActive
                      ? "bg-zinc-900 text-white"
                      : "text-zinc-400 hover:bg-zinc-100 hover:text-violet-700",
                  ].join(" ")
                }
              >
                <ListChecks className="h-3.5 w-3.5" strokeWidth={2} />
              </NavLink>
            </div>
            {projectsOpen && (
              <ul className="mt-1 flex flex-col gap-0.5">
                {projects.length === 0 && (
                  <li className="px-3 py-1 text-xs text-zinc-400">
                    {t("projects.empty_short")}
                  </li>
                )}
                {projects
                  .filter((p) => (isMe ? (p.open_for_me_count || 0) > 0 || p.has_overdue_for_me : true))
                  .map((p) => {
                  const isExpanded = expandedProjects.has(p.id);
                  const plists = projectLists[p.id];
                  const projCnt = isMe
                    ? p.open_for_me_count || 0
                    : p.open_count || 0;
                  const projOverdue = isMe ? !!p.has_overdue_for_me : !!p.has_overdue;
                  return (
                    <li key={p.id}>
                      <div className="flex items-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            toggleProjectExpansion(p.id);
                          }}
                          data-testid={`sidebar-project-expand-${p.id}`}
                          className="flex h-7 w-5 shrink-0 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                          aria-label={isExpanded ? "Grupları gizle" : "Grupları göster"}
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-3 w-3" />
                          ) : (
                            <ChevronRight className="h-3 w-3" />
                          )}
                        </button>
                        <NavLink
                          to={`/app/projects/${p.id}`}
                          data-testid={`sidebar-project-${p.id}`}
                          className={({ isActive }) =>
                            [
                              "flex flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors",
                              isActive && !activeListId
                                ? "bg-zinc-900 text-white"
                                : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900",
                            ].join(" ")
                          }
                        >
                          {({ isActive }) => (
                            <>
                              <Hash
                                className="h-3.5 w-3.5 shrink-0"
                                strokeWidth={1.75}
                              />
                              <span className="min-w-0 flex-1 truncate">
                                {p.name}
                              </span>
                              <span className="ml-auto flex shrink-0 items-center gap-1">
                                {projCnt > 0 && (
                                  <span
                                    data-testid={`sidebar-project-badge-${p.id}`}
                                    className={[
                                      "inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-semibold",
                                      isActive && !activeListId
                                        ? "bg-white text-zinc-900"
                                        : "bg-amber-500 text-white",
                                    ].join(" ")}
                                    title={`${projCnt} tamamlanmamış görevin`}
                                  >
                                    {projCnt}
                                  </span>
                                )}
                                {projOverdue && (
                                  <span
                                    data-testid={`sidebar-project-overdue-${p.id}`}
                                    title="Bu projede gecikmiş görev var"
                                    className="inline-flex"
                                  >
                                    <Zap
                                      className="h-3.5 w-3.5 fill-red-500 text-red-600"
                                      strokeWidth={2}
                                    />
                                  </span>
                                )}
                              </span>
                            </>
                          )}
                        </NavLink>
                      </div>

                      {isExpanded && (
                        <ul
                          data-testid={`sidebar-project-lists-${p.id}`}
                          className="ml-6 mt-0.5 flex flex-col gap-0.5 border-l border-zinc-200 pl-2"
                        >
                          {listsLoading[p.id] && !plists && (
                            <li className="px-2 py-1 text-[11px] text-zinc-400">
                              Yükleniyor...
                            </li>
                          )}
                          {plists && plists.length === 0 && (
                            <li className="px-2 py-1 text-[11px] text-zinc-400">
                              — grup yok —
                            </li>
                          )}
                          {plists
                            ?.filter((l) =>
                              isMe
                                ? (l.open_for_me_count || 0) > 0 ||
                                  l.has_overdue_for_me
                                : true,
                            )
                            .map((l) => {
                            const isListActive = activeListId === l.id;
                            const cnt = isMe
                              ? l.open_for_me_count || 0
                              : l.open_count || 0;
                            const overdue = isMe
                              ? !!l.has_overdue_for_me
                              : !!l.has_overdue;
                            const palette = getListPalette(l.color);
                            return (
                              <li key={l.id}>
                                <NavLink
                                  to={`/app/projects/${p.id}?list=${l.id}`}
                                  data-testid={`sidebar-list-${l.id}`}
                                  className={[
                                    "flex items-center gap-2 rounded-md px-2 py-1 text-xs transition-colors",
                                    isListActive
                                      ? "bg-zinc-900 text-white"
                                      : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800",
                                  ].join(" ")}
                                >
                                  {l.emoji ? (
                                    <span className="text-[13px] leading-none" aria-hidden>
                                      {l.emoji}
                                    </span>
                                  ) : (
                                    <span
                                      className={[
                                        "inline-block h-2 w-2 shrink-0 rounded-full",
                                        l.color
                                          ? palette.sidebarDot
                                          : isListActive
                                            ? "bg-white/50"
                                            : "bg-zinc-300",
                                      ].join(" ")}
                                      aria-hidden
                                    />
                                  )}
                                  <span className="min-w-0 flex-1 truncate">
                                    {l.name}
                                  </span>
                                  <span className="ml-auto flex shrink-0 items-center gap-1">
                                    {cnt > 0 && (
                                      <span
                                        data-testid={`sidebar-list-badge-${l.id}`}
                                        className={[
                                          "inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold",
                                          isListActive
                                            ? "bg-white text-zinc-900"
                                            : "bg-amber-500 text-white",
                                        ].join(" ")}
                                        title={`${cnt} tamamlanmamış görevin`}
                                      >
                                        {cnt}
                                      </span>
                                    )}
                                    {overdue && (
                                      <span
                                        data-testid={`sidebar-list-overdue-${l.id}`}
                                        title="Bu grupta gecikmiş görev var"
                                        className="inline-flex"
                                      >
                                        <Zap
                                          className="h-3.5 w-3.5 fill-red-500 text-red-600"
                                          strokeWidth={2}
                                        />
                                      </span>
                                    )}
                                  </span>
                                </NavLink>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {showProjectList && <TeamsSection projects={projects} />}
      </nav>

      <div className="mt-4 border-t border-zinc-200 pt-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="sidebar-settings-menu"
              className="group flex w-full items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-zinc-100"
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-900 text-xs font-semibold text-white">
                {(user?.name || "?").charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate text-sm font-medium text-zinc-900">
                  {user?.name}
                </div>
                <div className="truncate text-[11px] text-zinc-500">
                  {user?.email}
                </div>
              </div>
              <ChevronUp className="h-4 w-4 text-zinc-400" strokeWidth={1.75} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            side="top"
            className="w-56"
            data-testid="sidebar-settings-menu-content"
          >
            <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-zinc-500">
              {t("nav.settings_group")}
            </DropdownMenuLabel>
            <DropdownMenuItem asChild>
              <NavLink
                to="/app/notifications"
                data-testid={TEST_IDS.sidebarNotifPrefs}
                className="flex w-full cursor-pointer items-center gap-2"
              >
                <Bell className="h-4 w-4" strokeWidth={1.75} />
                <span>{t("nav.notifications")}</span>
              </NavLink>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <NavLink
                to="/app/motivasyon-kurallari"
                data-testid="sidebar-motivation-rules"
                className="flex w-full cursor-pointer items-center gap-2"
              >
                <Trophy className="h-4 w-4" strokeWidth={1.75} />
                <span>Motivasyon Kuralları</span>
              </NavLink>
            </DropdownMenuItem>
            {/* Ayarlar — all authenticated users. The Settings.jsx page
                already gates super-admin-only tabs (Organizasyon,
                Milestones, Smartsheet, Mailler) inside itself, so normal
                users just see Profil / Güvenlik / Bildirimler. */}
            <DropdownMenuItem asChild>
              <NavLink
                to="/app/settings"
                data-testid={TEST_IDS.sidebarSettings}
                className="flex w-full cursor-pointer items-center gap-2"
              >
                <SettingsIcon className="h-4 w-4" strokeWidth={1.75} />
                <span>{t("nav.settings")}</span>
              </NavLink>
            </DropdownMenuItem>
            {isSuperAdmin && (
              <>
                <DropdownMenuItem asChild>
                  <NavLink
                    to="/app/users"
                    data-testid={TEST_IDS.sidebarUsers}
                    className="flex w-full cursor-pointer items-center gap-2"
                  >
                    <Users className="h-4 w-4" strokeWidth={1.75} />
                    <span>{t("nav.users")}</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <NavLink
                    to="/app/billing"
                    data-testid={TEST_IDS.sidebarBilling}
                    className="flex w-full cursor-pointer items-center gap-2"
                  >
                    <CreditCard className="h-4 w-4" strokeWidth={1.75} />
                    <span>{t("nav.billing")}</span>
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <NavLink
                    to="/app/integrations"
                    data-testid={TEST_IDS.sidebarIntegrations}
                    className="flex w-full cursor-pointer items-center gap-2"
                  >
                    <Plug className="h-4 w-4" strokeWidth={1.75} />
                    <span>{t("nav.integrations")}</span>
                  </NavLink>
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onLogout}
              data-testid={TEST_IDS.logoutBtn}
              className="cursor-pointer text-zinc-700"
            >
              <LogOut className="mr-2 h-4 w-4" strokeWidth={1.75} />
              {t("auth.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}
